import { cn } from '../lib/cn';
import { currencySymbol, splitAmount } from '../lib/format';
import { toMajor } from '../contracts/money';
import type { CurrencyCode } from '../contracts/enums';

/**
 * Mercury-style money typography. Takes the **wire shape**: integer minor units
 * (poisha/cents) + currencyCode — the same shape the API emits — and converts to
 * display at this, the very last step.
 *
 *   <Money minor={123450} />                ৳ 1,234.⁵⁰
 *   <Money minor={-21311} />                −৳ 213.¹¹
 *   <Money minor={2850000} hideCents />     ৳ 28,500     (KPI tiles)
 */
export function Money({
  minor,
  currencyCode = 'BDT',
  hideCents = false,
  tone = 'auto',
  className,
}: {
  /** Integer minor units (poisha/cents). */
  minor: number;
  currencyCode?: CurrencyCode;
  hideCents?: boolean;
  /** 'auto' = green for positive, red for negative; 'neutral' = always default text color */
  tone?: 'auto' | 'neutral' | 'positive' | 'negative';
  className?: string;
}) {
  const { sign, integer, cents } = splitAmount(toMajor(minor));
  const sym = currencySymbol(currencyCode);

  const colorCls =
    tone === 'auto'
      ? minor < 0
        ? 'text-rose-600 dark:text-rose-300'
        : 'text-emerald-700 dark:text-emerald-300'
      : tone === 'positive'
      ? 'text-emerald-700 dark:text-emerald-300'
      : tone === 'negative'
      ? 'text-rose-600 dark:text-rose-300'
      : '';

  return (
    <span className={cn('tabular-nums whitespace-nowrap', colorCls, className)}>
      {sign}
      {sym}
      {sym.length === 1 ? ' ' : ''}
      {integer}
      {!hideCents && (
        <span className="text-[0.7em] align-super opacity-75 ml-px">.{cents}</span>
      )}
    </span>
  );
}
